import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Shield, Eye, EyeOff, Lock, UserCheck, Trash2, Database, Settings, Globe, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    username: '',
    date_of_birth: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaExpected, setCaptchaExpected] = useState('');

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion(`What is ${num1} + ${num2}?`);
    setCaptchaExpected(String(num1 + num2));
    setCaptchaAnswer('');
    setCaptchaVerified(false);
  };

  const verifyCaptcha = () => {
    if (captchaAnswer === captchaExpected) {
      setCaptchaVerified(true);
      toast.success('Verified!');
    } else {
      toast.error('Incorrect answer. Try again.');
      generateCaptcha();
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error('Please complete the verification');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!formData.date_of_birth) {
      toast.error('Please enter your date of birth');
      return;
    }
    
    const dob = new Date(formData.date_of_birth);
    const age = Math.floor((new Date() - dob) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 13) {
      toast.error('You must be at least 13 years old to use Socyy');
      return;
    }
    
    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await axios.post(`${API_URL}/auth/register`, {
        ...registerData,
        captcha_token: 'verified'
      }, {
        withCredentials: true,
      });
      toast.success('Account created! Please verify your identity.');
      // Navigate to liveness verification before onboarding
      navigate('/verify/liveness', { 
        state: { 
          user: response.data.user, 
          fromRegistration: true,
          nextStep: '/onboarding'
        } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      generateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8">
        {/* Left Column - Data Trust & Privacy Promise */}
        <div className="hidden lg:flex flex-col justify-center space-y-6 px-4">
          <div className="mb-4">
            <h2 className="text-3xl font-heading font-black text-gray-900 mb-2">
              Your Data, Your Rules
            </h2>
            <p className="text-lg text-gray-600">
              At Socyy, we believe you should have complete control over your digital life.
            </p>
          </div>
          
          {/* Trust Badges */}
          <div className="space-y-4">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-green-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Ultimate Data Control</h3>
                  <p className="text-sm text-gray-600">
                    Paid users have full control over how their data is used, shared, and who can see it. 
                    Customize every privacy setting to match your comfort level.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Right to Delete — Always</h3>
                  <p className="text-sm text-gray-600">
                    Every user—free or paid—can permanently delete their account and all associated data at any time. 
                    No questions asked, no data kept.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-purple-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">End-to-End Encrypted DMs</h3>
                  <p className="text-sm text-gray-600">
                    Your private messages are encrypted so only you and the recipient can read them. 
                    Not even Socyy can access your conversations.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-amber-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">GDPR & CCPA Compliant</h3>
                  <p className="text-sm text-gray-600">
                    We follow strict global privacy regulations. Request your data export, 
                    opt-out of data sales, and exercise all your legal privacy rights.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trust Promise */}
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-5 border-2 border-primary/30">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span className="font-bold text-primary">Our Promise to You</span>
            </div>
            <p className="text-sm text-gray-700">
              We will never sell your data to third parties. We will never use your data for targeted advertising without your explicit consent. 
              Your trust is our most valuable asset.
            </p>
          </div>
        </div>
        
        {/* Right Column - Registration Form */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20">
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-3xl font-heading font-black text-gray-900">Socyy</h1>
            </div>

            <h2 className="text-2xl font-heading font-bold text-center mb-6 text-gray-900" data-testid="register-title">
              Join Socyy Today!
            </h2>
            
            {/* Mobile Trust Badge (visible only on small screens) */}
            <div className="lg:hidden mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="font-bold text-green-800 text-sm">Your Data, Your Control</span>
              </div>
              <p className="text-xs text-green-700">
                At Socyy, you control your data. Delete anytime, E2E encrypted DMs, and GDPR/CCPA compliant.
              </p>
            </div>

          <form onSubmit={handleRegister} className="space-y-4 mb-6" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="font-semibold text-gray-700">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl border-2 h-12 mt-1"
                required
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="username" className="font-semibold text-gray-700">Username</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="rounded-xl border-2 h-12 mt-1"
                required
                data-testid="register-username-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="font-semibold text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-xl border-2 h-12 mt-1"
                required
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-semibold text-gray-700">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="rounded-xl border-2 h-12 pr-12"
                  required
                  minLength={8}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="font-semibold text-gray-700">Confirm Password</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="rounded-xl border-2 h-12 pr-12"
                  required
                  minLength={8}
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="date_of_birth" className="font-semibold text-gray-700">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="rounded-xl border-2 h-12 mt-1"
                required
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 13)).toISOString().split('T')[0]}
                data-testid="register-dob-input"
              />
              <p className="text-xs text-gray-500 mt-1">You must be at least 13 years old</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-primary" />
                <Label className="font-semibold text-gray-700">Verify You're Human</Label>
              </div>
              <p className="text-sm text-gray-700 mb-2 font-medium">{captchaQuestion}</p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && verifyCaptcha()}
                  className="rounded-xl border-2 h-10"
                  placeholder="Your answer"
                  disabled={captchaVerified}
                  data-testid="captcha-input"
                />
                {!captchaVerified && (
                  <Button
                    type="button"
                    onClick={verifyCaptcha}
                    className="rounded-xl bg-primary hover:bg-primary-700 text-white font-bold"
                    data-testid="verify-captcha-btn"
                  >
                    Verify
                  </Button>
                )}
                {captchaVerified && (
                  <div className="flex items-center gap-2 text-green-600 font-bold">
                    ✓ Verified
                  </div>
                )}
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !captchaVerified}
              className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleSignup}
            variant="outline"
            className="w-full rounded-full border-2 h-12 font-bold hover:bg-gray-50 transition-all"
            data-testid="register-google-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-primary hover:text-primary-700" data-testid="register-login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  </div>
  );
};

export default Register;
